import { useEffect } from 'react';
import {
  EventData,
  Map,
  MapboxGeoJSONFeature,
  MapEventType,
  PointLike,
} from 'mapbox-gl';
import bbox from '@turf/bbox';
import { Feature, featureCollection } from '@turf/helpers';
import {
  LineString,
  centroid,
  lineString,
  point,
  pointToLineDistance,
  distance as turfDistance,
} from '@turf/turf';
import useMap from 'hooks/useMap';
import { MapTypes } from 'reducers/menuReducer';
import { RegionDto } from 'model/RegionDto';
import { SOURCE_ID } from './MapStyle/utils';

function panToFeatures(
  map: Map,
  features: Feature[] | undefined,
  animate: boolean,
) {
  if (!features || features.length === 0) return;
  const regionsCollection = featureCollection(features);
  const regionsBbox = bbox(regionsCollection);
  map.fitBounds(regionsBbox as any, { padding: 100, animate });
}

export const centerOnRegions = (
  map: Map,
  dtoRegions: Feature[],
  animate = false,
) => panToFeatures(map, dtoRegions, animate);

export const queryFeatures = (
  e: MapEventType['click'] & EventData,
  layers: string[],
  distance = 10,
) => {
  const map = e.target;
  if (!map.getLayer(layers[0])) {
    return [];
  }
  const bbox: [PointLike, PointLike] = [
    [e.point.x - distance, e.point.y - distance],
    [e.point.x + distance, e.point.y + distance],
  ];
  let features = map.queryRenderedFeatures(bbox, {
    layers,
  });

  const mousePoint = point([e.lngLat.lng, e.lngLat.lat]);

  const featuresSorted = features
    .map((it: MapboxGeoJSONFeature) => {
      return {
        distance:
          it.geometry.type == 'Point'
            ? turfDistance(mousePoint, it.geometry as any)
            : pointToLineDistance(mousePoint, it.geometry as any),
        feature: it,
      };
    })
    .sort((a, b) => a.distance - b.distance);

  return featuresSorted;
};

export function CenterOnRegions({ regions }: { regions?: RegionDto[] }) {
  const map = useMap();

  useEffect(() => {
    centerOnRegions(map, regions);
  }, [map]);

  return null;
}

export function validateLatLng(lat: number, lng: number) {
  return lat && lng && Math.abs(lat) <= 90 && Math.abs(lng) <= 180;
}

export const lineStringToReverseGeocodePoint = (
  lineString: Feature<LineString>,
) => centroid(lineString).geometry.coordinates as [number, number];

export const toTurfFeature = (
  feature: MapboxGeoJSONFeature,
): Feature<LineString> => {
  const geometry = (feature.geometry as unknown) as any;

  return lineString(geometry.coordinates, {
    ...feature.properties,
  });
};

export const isLayerLineString = ['==', ['geometry-type'], 'LineString'];

export const getFirstLabelLayer = (
  map: mapboxgl.Map,
  checkFor: 'road-labels' | 'shields' | 'road-labels-and-shields',
  mapTypeName: MapTypes = 'Genesis',
) => {
  const labelLayerIds: {
    [key in MapTypes]: { roadLabels: string; shields: string };
  } = {
    Genesis: {
      roadLabels: 'road_label',
      shields: 'shield',
    },
    Orbis: {
      roadLabels: 'road_label',
      shields: 'shield',
    },
  };
  const groups: string[] = [];

  if (checkFor === 'road-labels' || checkFor === 'road-labels-and-shields') {
    groups.push(labelLayerIds[mapTypeName].roadLabels);
  }

  if (checkFor === 'shields' || checkFor === 'road-labels-and-shields') {
    groups.push(labelLayerIds[mapTypeName].shields);
  }

  return map
    .getStyle()
    .layers.find(
      (layer) =>
        layer.type !== 'custom' &&
        layer.source === SOURCE_ID &&
        groups.includes(layer.metadata?.group ?? ''),
    )?.id;
};
